import { Balances, TokenGateway } from './ports';

export type WrapStep = 'approving' | 'wrapping' | 'unwrapping';
export type WrapResult = { ok: true } | { ok: false; reason: 'balance' | 'rejected' } | { ok: false; reason: 'failed'; message: string };

const isRejection = (e: unknown) => {
  const err = e as { name?: string; message?: string; code?: number };
  return err?.name === 'UserRejectedRequestError' || err?.code === 4001 || /rejected|denied/i.test(err?.message || '');
};

const failed = (e: unknown): WrapResult =>
  isRejection(e) ? { ok: false, reason: 'rejected' } : { ok: false, reason: 'failed', message: (e as Error)?.message || 'Something went wrong' };

/** ZEUS to wZEUS, 1:1. Approves exactly the amount first when needed, never unlimited. */
export async function wrapZeus({ amount, gateway, onStep = () => {} }: { amount: bigint; gateway: TokenGateway; onStep?: (s: WrapStep) => void }): Promise<WrapResult> {
  try {
    const b = await gateway.read();
    if (amount > b.zeus) return { ok: false, reason: 'balance' };
    if (b.allowance < amount) {
      onStep('approving');
      await gateway.approve(amount);
    }
    onStep('wrapping');
    await gateway.wrap(amount);
    return { ok: true };
  } catch (e) {
    return failed(e);
  }
}

/** wZEUS back to ZEUS, 1:1. */
export async function unwrapZeus({ amount, gateway, onStep = () => {} }: { amount: bigint; gateway: TokenGateway; onStep?: (s: WrapStep) => void }): Promise<WrapResult> {
  try {
    const b = await gateway.read();
    if (amount > b.wzeus) return { ok: false, reason: 'balance' };
    onStep('unwrapping');
    await gateway.unwrap(amount);
    return { ok: true };
  } catch (e) {
    return failed(e);
  }
}

/** Holding wZEUS with nobody to vote for it: the votes do not count until activated. */
export const needsActivation = (b: Balances): boolean => b.wzeus > BigInt(0) && !b.delegate;

/** Delegates the wallet's wZEUS votes to itself, once, so they count on Tally. */
export async function activateVotes({ gateway }: { gateway: TokenGateway }): Promise<WrapResult> {
  try {
    await gateway.delegateToSelf();
    return { ok: true };
  } catch (e) {
    return failed(e);
  }
}
